import { styled } from "goober";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, Ref, RefObject, ReactNode } from "react";
import { createPortal } from "react-dom";
import type { IconName } from "../../assets/icons";
import { useScrollLock } from "../../hooks/useScrollLock";
import { blockRowNavProps } from "../../lib/eventHelpers";
import { zIndexes } from "../../lib/zIndexes";
import { ClearButton } from "../ClearButton";
import { CountBadge } from "../CountBadge";
import { Icon, type Size } from "../Icon";
import { Input } from "../Input";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

export const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const Header = styled("div")`
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 24px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

export const HeaderActions = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding-right: 12px;
`;

const SearchContainer = styled("div")`
  position: relative;
  margin-left: auto;
`;

const SearchIcon = styled("div")`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-primary);
  pointer-events: none;
`;

export const ScrollArea = styled("div")`
  flex: 1;
  overflow: auto;
  overscroll-behavior: none;
`;

export const ModalContent = styled("div")`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 400px;
`;

export const ModalHeader = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

export const ModalHeaderRight = styled("div")`
  margin-left: auto;
`;

export const ModalBody = styled("div")`
  flex: 1;
  padding: 24px;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

export const ModalFooter = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const DetailLabel = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const DetailRowRight = styled("div")`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  justify-content: flex-end;
`;

export const FieldLabel = styled("div")`
  display: flex;
  flex-direction: column;
`;

export const Card = styled("div")`
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  min-width: 0;
`;

export const SectionHeading = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 8px;
`;

const PillBadgeRoot = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 20px;
  padding: 0 8px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  font-size: 10px;
  font-weight: 450;
  line-height: 12px;
  letter-spacing: -1%;
  white-space: nowrap;
  max-width: 100%;

  &[data-interactive="true"] {
    cursor: pointer;
    &:hover {
      background: var(--alpha8);
    }
  }
`;

const PillBadgeIcon = styled("span")`
  flex-shrink: 0;
  display: flex;
  margin-left: -2px;
`;

const PillBadgeTrailingIcon = styled("span")`
  flex-shrink: 0;
  display: flex;
  margin-right: -2px;
`;

const PillBadgeLabel = styled("span")`
  overflow: hidden;
  text-overflow: ellipsis;
`;

export function PillBadge({
  icon,
  trailingIcon,
  children,
  style,
  interactive,
}: {
  icon?: IconName;
  trailingIcon?: IconName;
  children: ReactNode;
  style?: CSSProperties;
  interactive?: boolean;
}) {
  return (
    <PillBadgeRoot style={style} data-interactive={interactive || undefined}>
      {icon && (
        <PillBadgeIcon>
          <Icon name={icon} size={10} />
        </PillBadgeIcon>
      )}
      <PillBadgeLabel>{children}</PillBadgeLabel>
      {trailingIcon && (
        <PillBadgeTrailingIcon>
          <Icon name={trailingIcon} size={10} />
        </PillBadgeTrailingIcon>
      )}
    </PillBadgeRoot>
  );
}

export function ClickablePillBadge({
  icon,
  children,
  style,
  onClick,
}: {
  icon?: IconName;
  children: ReactNode;
  style?: CSSProperties;
  onClick: () => void;
}) {
  return (
    <PillBadgeRoot
      style={style}
      data-interactive="true"
      {...blockRowNavProps(onClick)}
    >
      {icon && (
        <PillBadgeIcon>
          <Icon name={icon} size={10} />
        </PillBadgeIcon>
      )}
      <PillBadgeLabel>{children}</PillBadgeLabel>
    </PillBadgeRoot>
  );
}

export const IconBadge = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BADGE_COLORS = [
  "sky",
  "red",
  "green",
  "orange",
  "purple",
  "pink",
  "teal",
] as const;

export type BadgeColor = (typeof BADGE_COLORS)[number];

function colorForId(id: string): BadgeColor {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return BADGE_COLORS[Math.abs(hash) % BADGE_COLORS.length]!;
}

export function ColoredIconBadge({
  id,
  iconName,
  size = 32,
  iconSize = 16,
}: {
  id: string;
  iconName: IconName;
  size?: number;
  iconSize?: Size;
}) {
  const color = colorForId(id);
  return (
    <IconBadge
      style={{
        background: `var(--${color}-200)`,
        width: size,
        height: size,
        borderRadius: size / 4,
      }}
    >
      <Icon name={iconName} size={iconSize} color={`var(--${color}-600)`} />
    </IconBadge>
  );
}

export function NeutralIconBadge({
  iconName,
  size = 32,
  iconSize = 16,
}: {
  iconName: IconName;
  size?: number;
  iconSize?: Size;
}) {
  return (
    <IconBadge
      style={{
        background: "var(--alpha4)",
        width: size,
        height: size,
        borderRadius: size / 4,
      }}
    >
      <Icon name={iconName} size={iconSize} color="var(--text-primary)" />
    </IconBadge>
  );
}

const HeaderSearch = styled(SearchContainer)`
  margin-left: 0;
`;

const BuiltinBadge = styled("span")`
  display: inline-flex;
  color: var(--text-primary);
  opacity: 0.3;
  &:hover {
    opacity: 0.4;
  }
`;

export function BuiltinLockBadge() {
  return (
    <Tooltip content="3B default">
      <BuiltinBadge>
        <Icon name="lock-filled" size={12} />
      </BuiltinBadge>
    </Tooltip>
  );
}

export { HelpTooltip } from "../HelpTooltip";

export const GROUP_TOOLTIP_COPY =
  "A group is a set of members. Give the group a role and everyone in it gets it.";

export const NAME_TOOLTIP_COPY =
  "Your name can’t be edited here. If you sign in with an identity provider, update your name there. If you sign in with an email link, it’s the name you entered when you first signed in, and can’t be changed.";

export const PERMISSIONS_TOOLTIP_COPY =
  "The specific actions a role allows, like reading or editing.";

export function SearchField({
  placeholder,
  value,
  onChange,
  width,
  autoFocus,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  width?: number;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // A dialog grabs focus on open (Modal calls el.focus()), so re-focus on the
  // next frame so the user can start typing immediately.
  useEffect(() => {
    if (!autoFocus) return;
    const id = requestAnimationFrame(() => ref.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [autoFocus]);
  return (
    <HeaderSearch>
      <SearchIcon>
        <Icon name="search" size={12} />
      </SearchIcon>
      <Input
        ref={ref}
        data-search
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={width != null ? { width, minWidth: width } : undefined}
      />
      {value && (
        <ClearButton onPress={() => onChange("")}>
          <Icon name="x" size={12} />
        </ClearButton>
      )}
    </HeaderSearch>
  );
}

export function HeaderCountBadge({ count }: { count: number }) {
  return (
    <CountBadge
      style={{
        background: "var(--alpha4)",
        height: 20,
        padding: "0 6px",
        margin: "0 0 0 6px",
      }}
    >
      <Text variant="text-smaller-md" color="var(--text-secondary)">
        {count}
      </Text>
    </CountBadge>
  );
}

export const RowNameLayout = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const RowNameContent = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const RowNameLine = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const SearchBar = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 12px 0 16px;
  gap: 8px;
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-primary);
  transition: box-shadow 100ms ease-out;
  cursor: text;

  &:has(input:focus) {
    outline: 0.5px solid var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
  }
`;

export const SearchInputWrap = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SearchIconWrap = styled("span")`
  display: flex;
  color: var(--text-primary);
  flex-shrink: 0;
`;

export const PlainInput = styled("input")`
  all: unset;
  flex: 1;
  min-width: 0;
  font-family: InterVariable;
  font-size: 12px;
  line-height: 16px;
  color: var(--text-primary);
  &::placeholder {
    opacity: 0.3;
  }
  &:focus::placeholder {
    opacity: 0.6;
  }
`;

export const MatchSectionLabel = styled("div")`
  padding: 8px 8px 12px;
`;

export const MatchRow = styled("button")`
  all: unset;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  width: 100%;
  cursor: pointer;
  box-sizing: border-box;
  &:hover,
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

export const ListBox = styled("div")`
  flex: 1;
  min-height: 0;
  min-width: 0;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: auto;

  &[data-compact="true"] {
    [role="gridcell"] {
      height: 64px;
    }
    [role="gridcell"]:first-child,
    [role="columnheader"]:first-child {
      padding-left: 16px;
    }
    [role="gridcell"]:last-child,
    [role="columnheader"]:last-child {
      padding-right: 16px;
    }
    [role="row"]:last-of-type > [role="gridcell"] {
      border-bottom: 0;
    }
  }
`;

export const DetailList = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

export const DetailRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  min-height: 56px;
  box-sizing: border-box;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  &[data-interactive="true"] {
    cursor: pointer;
    user-select: none;
  }
  &[data-interactive="true"]:hover {
    background: var(--surface-hover);
  }
`;

const AnchoredListPopoverShell = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  box-shadow: var(--elevation-low);
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 4px;
`;

const POPOVER_GAP = 6;
const POPOVER_VIEWPORT_MARGIN = 8;
const POPOVER_PREFERRED_HEIGHT = 280;

type PopoverPlacement = {
  left: number;
  width: number;
  maxHeight: number;
  openUp: boolean;
  top?: number;
  bottom?: number;
};

function computePlacement(rect: DOMRect): PopoverPlacement {
  const spaceBelow =
    window.innerHeight - rect.bottom - POPOVER_GAP - POPOVER_VIEWPORT_MARGIN;
  const spaceAbove = rect.top - POPOVER_GAP - POPOVER_VIEWPORT_MARGIN;
  const openUp =
    spaceBelow < POPOVER_PREFERRED_HEIGHT && spaceAbove > spaceBelow;
  return {
    left: rect.left,
    width: rect.width,
    maxHeight: Math.min(
      POPOVER_PREFERRED_HEIGHT,
      Math.max(openUp ? spaceAbove : spaceBelow, 0)
    ),
    openUp,
    ...(openUp
      ? { bottom: window.innerHeight - rect.top + POPOVER_GAP }
      : { top: rect.bottom + POPOVER_GAP }),
  };
}

// Portaled to the nearest popover host so it isn't clipped by a scrolling
// modal body; positioned below `anchorRef` to match its width. Flips above the
// anchor when there isn't room below, and clamps its height to the available
// space so a long list scrolls instead of overflowing the viewport. Recomputes
// on scroll/resize (capture-phase, to catch the modal body scrolling) so it
// tracks the anchor.
export function AnchoredListPopover({
  anchorRef,
  open,
  popoverRef,
  children,
}: {
  anchorRef: RefObject<HTMLDivElement | null>;
  open: boolean;
  popoverRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  const [placement, setPlacement] = useState<PopoverPlacement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setPlacement(null);
      return;
    }
    const reposition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setPlacement(computePlacement(rect));
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, anchorRef]);

  useScrollLock(open);

  if (!open || !placement) return null;
  const target =
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    anchorRef.current?.closest("[data-popover-host], dialog") ?? document.body;

  return createPortal(
    <AnchoredListPopoverShell
      ref={popoverRef}
      data-popover
      data-direction={placement.openUp ? "up" : "down"}
      style={{
        left: placement.left,
        width: placement.width,
        maxHeight: placement.maxHeight,
        top: placement.top,
        bottom: placement.bottom,
        zIndex: zIndexes.dropdown,
      }}
    >
      {children}
    </AnchoredListPopoverShell>,
    target
  );
}

export const settingsModalStyle = {
  width: 680,
  height: 760,
  display: "flex",
  flexDirection: "column",
} as const;

export function HoverActions({
  visible,
  children,
}: {
  visible: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{ opacity: visible ? 1 : 0 }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {children}
    </div>
  );
}

export const DropdownOpener = styled("button")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 12px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 8px !important;
  background: var(--surface-primary);
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:disabled {
    background: var(--alpha4);
    cursor: not-allowed;
  }
`;
